import type { Summary, KeyConcept, SectionRef } from '@/shared/types';
import { Card } from '@/components/Card';

export function SummaryPanel({
  summary,
  onAnchorClick,
}: {
  summary: Summary | undefined;
  onAnchorClick: (anchor: string, kind: 'timestamp' | 'heading') => void;
}) {
  if (!summary) {
    return (
      <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
        No summary yet. Generate one above.
      </div>
    );
  }

  const concepts = summary.key_concepts ?? [];

  return (
    <div className="space-y-3">
      <div>
        <h2 className="mb-1 text-sm font-semibold text-slate-700">TL;DR</h2>
        <p className="rounded bg-slate-50 p-3 text-sm leading-relaxed text-slate-800">
          {summary.tldr}
        </p>
      </div>

      <div>
        <h2 className="mb-1 text-sm font-semibold text-slate-700">Critical sections</h2>
        <ul className="space-y-1 text-sm">
          {summary.key_sections.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <button
                onClick={() => onAnchorClick(s.anchor, s.kind)}
                className="rounded bg-blue-50 px-2 py-0.5 font-mono text-xs text-blue-700 hover:bg-blue-100"
                title={
                  s.kind === 'timestamp'
                    ? 'Jump to this timestamp in the transcript below.'
                    : 'Jump to this heading in the chapter text below.'
                }
              >
                {s.anchor}
              </button>
              <span className="text-slate-700">{s.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {concepts.length > 0 && (
        <div>
          {/* Quieter heading than the two above — visual weight matches the
              hierarchy: TL;DR (strongest) → critical sections → concepts. */}
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Key concepts ({concepts.length})
          </h2>
          <Card padding="none">
            {concepts.map((c, i) => (
              <ConceptRow
                key={i}
                concept={c}
                defaultOpen={i === 0}
                onAnchorClick={onAnchorClick}
              />
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function ConceptRow({
  concept,
  defaultOpen,
  onAnchorClick,
}: {
  concept: KeyConcept;
  defaultOpen: boolean;
  onAnchorClick: (anchor: string, kind: 'timestamp' | 'heading') => void;
}) {
  // Sort refs so headings render before timestamps — text-then-video matches
  // the convention in lecture-capture/study tools (Panopto, Echo360, Kortext).
  const refs = [...concept.refs].sort((a, b) =>
    a.kind === b.kind ? 0 : a.kind === 'heading' ? -1 : 1
  );
  return (
    <details
      open={defaultOpen}
      className="group border-b border-slate-100 last:border-b-0"
    >
      <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-800">
          <span className="mr-1 inline-block text-slate-400 group-open:rotate-90 transition">
            ›
          </span>
          {concept.title}
        </h3>
        <div className="flex flex-wrap gap-1">
          {refs.map((r, i) => (
            <RefBtn key={i} ref_={r} onClick={() => onAnchorClick(r.anchor, r.kind)} />
          ))}
        </div>
      </summary>
      <p className="px-3 pb-3 text-sm leading-relaxed text-slate-700">{concept.summary}</p>
    </details>
  );
}

function RefBtn({ ref_, onClick }: { ref_: SectionRef; onClick: () => void }) {
  // Stop propagation so the chip click doesn't toggle the parent <details>.
  const isTime = ref_.kind === 'timestamp';
  const label = ref_.source_label
    ? `${isTime ? '▶' : '§'} ${ref_.anchor} · ${ref_.source_label}`
    : `${isTime ? '▶' : '§'} ${ref_.anchor}`;
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${
        isTime
          ? 'bg-purple-50 text-purple-700 hover:bg-purple-100'
          : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
      }`}
      title={
        isTime
          ? 'Jump to this timestamp in the transcript.'
          : 'Jump to this heading in the chapter text.'
      }
    >
      {label}
    </button>
  );
}
